import { faInfoCircle as InfoLight } from '@fortawesome/pro-light-svg-icons';
import {
  faChevronUp as ExpandIcon,
  faInfoCircle
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useAtomValue } from 'jotai';

import SOURCE_TYPE, {
  SOURCE_TYPES_WITH_INFO_BLOCK
} from '../SourceCreate.constants';
import { useFetchSupportedLogSources } from '../SourceCreate.service';
import { supportedLogSourcesAtom } from '../SourceCreate.store';
import { getUiMetaData } from '../SourceCreate.utils';
import useStyles from './SourceInfoBlock.styles';
import type {
  SourceInfoBlockProps,
  SupportedLogSourceModel
} from './SourceInfoBlock.types';

const SourceInfoBlock = ({ sourceTypeId, subSource }: SourceInfoBlockProps) => {
  const { classes } = useStyles();
  const supportsLogSourceInfo = SOURCE_TYPES_WITH_INFO_BLOCK.includes(
    sourceTypeId as (typeof SOURCE_TYPES_WITH_INFO_BLOCK)[number]
  );
  const isEntraId = sourceTypeId === SOURCE_TYPE.MICROSOFT_ENTRA_ID;
  const supportedLogSourcesQuery = useFetchSupportedLogSources(sourceTypeId);
  const supportedLogSources = useAtomValue(supportedLogSourcesAtom);

  const meta = getUiMetaData(sourceTypeId, subSource);
  const showSupportedLogSourcesPanel =
    supportsLogSourceInfo && supportedLogSourcesQuery.isSuccess;

  const showEntraFooter =
    isEntraId &&
    (subSource === 'GraphAPI' || subSource === 'O365API') &&
    Boolean(meta?.footer_note);

  if (!meta) return null;
  if (!showSupportedLogSourcesPanel && !showEntraFooter) return null;

  return (
    <Grid
      className={classes.sourceInfoBlockGridWrapper}
      size={{
        xs: 12,
        sm: showSupportedLogSourcesPanel ? 12 : 6,
        md: showSupportedLogSourcesPanel ? 12 : 6
      }}
    >
      {showSupportedLogSourcesPanel && (
        <Box className={classes.sourceInfoBlockWrapper}>
          <Accordion
            className={classes.sourceInfoBlockPanelAccordion}
            defaultExpanded
          >
            {meta.title && (
              <AccordionSummary
                className={classes.sourceInfoBlockPanelAccordionSummary}
                expandIcon={<FontAwesomeIcon icon={ExpandIcon} size="sm" />}
                id="panel-details-header"
              >
                <FontAwesomeIcon
                  size="lg"
                  className={classes.sourceInfoBlockTitleInfoIcon}
                  icon={faInfoCircle}
                />
                {meta.title}
              </AccordionSummary>
            )}

            <AccordionDetails
              className={classes.sourceInfoBlockAccordionDetails}
            >
              {supportedLogSources.length > 0 ? (
                <Grid
                  container
                  spacing={2}
                  className={classes.supportedLogSourcesList}
                >
                  {supportedLogSources.map((src: SupportedLogSourceModel) => (
                    <Grid size={{ xs: 12, lg: 4 }} key={src.display_name}>
                      <Typography variant="body2">
                        {src.display_name}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2">
                  No supported log sources available.
                </Typography>
              )}
            </AccordionDetails>

            {meta.footer_note && (
              <Typography
                component="div"
                variant="caption"
                sx={{ ps: 2, p: 1 }}
                className={classes.sourceInfoBlockFooterText}
              >
                {meta.footer_note}
              </Typography>
            )}
          </Accordion>
        </Box>
      )}

      {showEntraFooter && (
        <Typography
          variant="caption"
          className={classes.sourceInfoBlockFooterText}
        >
          <FontAwesomeIcon
            size="lg"
            className={classes.sourceInfoBlockFooterInfoIcon}
            icon={InfoLight}
          />
          {meta.footer_note}
        </Typography>
      )}
    </Grid>
  );
};

export default SourceInfoBlock;
